import React, { useEffect, useState } from 'react';
import { Gift, Copy, Share2, Users, Coins, CheckCircle2 } from 'lucide-react';
import { applicantAPI } from '@/services/applicantAPI';

export default function ApplicantReferral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    applicantAPI.getReferral()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || 'Failed to load'); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* ignore */ }
  };

  const share = async () => {
    if (!data) return;
    const shareData = {
      title: 'Join Numuv — AI recruitment for the modern job seeker',
      text: `I'm on Numuv — an AI-powered career platform. Sign up with my code ${data.referral_code} and we both earn Mu Points!`,
      url: data.share_link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* dismissed */ }
    } else {
      copy(`${shareData.text} ${shareData.url}`, 'share');
    }
  };

  if (loading) {
    return <div className="text-slate-500 dark:text-zinc-500 text-sm py-10" data-testid="referral-loading">Loading your referral code…</div>;
  }
  if (error || !data) {
    return <div className="text-red-600 dark:text-red-400 text-sm py-10" data-testid="referral-error">{error || 'No data'}</div>;
  }

  return (
    <div data-testid="applicant-referral">
      <div className="flex items-start gap-3 mb-6">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <Gift className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Refer &amp; Earn</h1>
          <p className="text-sm text-slate-500 dark:text-zinc-500 mt-0.5">
            Share your code — earn <span className="font-medium text-emerald-600 dark:text-emerald-400">{data.referral_bonus_mu} Mu</span> for every friend who joins.
            They&apos;ll get <span className="font-medium text-emerald-600 dark:text-emerald-400">{data.signup_bonus_mu + data.referral_acceptance_mu} Mu</span> at signup.
          </p>
        </div>
      </div>

      {/* Hero: referral code card */}
      <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 rounded-xl p-6 mb-6" data-testid="referral-code-card">
        <p className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-2">Your referral code</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-4xl font-mono font-bold text-slate-900 dark:text-white tracking-widest" data-testid="referral-code-value">
            {data.referral_code}
          </div>
          <button
            onClick={() => copy(data.referral_code, 'code')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/70 dark:bg-zinc-900/70 border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-zinc-900"
            data-testid="copy-code-btn"
          >
            {copied === 'code' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'code' ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="mt-5">
          <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1.5">Or share your unique signup link</p>
          <div className="flex flex-wrap gap-2">
            <input
              readOnly
              value={data.share_link}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-zinc-300 font-mono"
              data-testid="referral-share-link"
            />
            <button
              onClick={() => copy(data.share_link, 'link')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-200 hover:bg-slate-200 dark:hover:bg-zinc-700"
              data-testid="copy-link-btn"
            >
              {copied === 'link' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied === 'link' ? 'Copied' : 'Copy link'}
            </button>
            <button
              onClick={share}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid="stat-total-referred">
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-500 mb-1">
            <Users className="w-3.5 h-3.5" /> Friends referred
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white">{data.total_referred}</div>
        </div>
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid="stat-total-earned">
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-500 mb-1">
            <Coins className="w-3.5 h-3.5" /> Mu Points earned
          </div>
          <div className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">{data.total_earned_mu}</div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-3">How it works</h3>
        <ol className="space-y-2.5 text-sm text-slate-600 dark:text-zinc-400 list-decimal list-inside">
          <li>Share your code or link with someone who wants to find their next role.</li>
          <li>They sign up on Numuv and enter your code — <span className="text-emerald-600 dark:text-emerald-400 font-medium">{data.referral_acceptance_mu} bonus Mu</span> is added on top of their {data.signup_bonus_mu}-Mu signup bonus.</li>
          <li>You earn <span className="text-emerald-600 dark:text-emerald-400 font-medium">{data.referral_bonus_mu} Mu</span> the moment their signup is complete.</li>
        </ol>
      </div>

      {/* Recent referrals list */}
      {data.recent_referrals && data.recent_referrals.length > 0 && (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg" data-testid="recent-referrals">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-zinc-800/50">
            <h3 className="text-sm font-medium text-slate-900 dark:text-white">Recent referrals</h3>
          </div>
          <ul className="divide-y divide-slate-200 dark:divide-zinc-800/50">
            {data.recent_referrals.map((r) => (
              <li key={r.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`referred-${r.id}`}>
                <span className="text-slate-700 dark:text-zinc-300 font-mono">{r.email_masked}</span>
                <span className="text-xs text-slate-500 dark:text-zinc-500">
                  {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
